import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Calendar,
  Check,
  Clock,
  Crown,
  Eye,
  MessageSquare,
  Scissors,
  Sparkles,
  TrendingUp,
  Upload,
  UserPlus,
  Wallet,
} from "lucide-react";
import SEO, { breadcrumbSchema } from "../Components/SEO";
import TheNav from "../Components/navbar";
import Footer from "../Components/footer";
import ss1 from "../images/ss1.webp";

const NEON = "#84ff00";
const PURPLE = "#9146FF";
const PURPLE_DEEP = "#7a3ae7";
const BG = "#07060d";
const SURFACE = "rgba(255,255,255,0.04)";
const HAIRLINE = "rgba(255,255,255,0.08)";
const GRID_BG =
  "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)";

function Section({ children, className = "" }) {
  return (
    <section className={`mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 sm:py-28 ${className}`}>
      {children}
    </section>
  );
}

function CTAButton({ to, variant = "primary", children }) {
  const base =
    "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5";

  if (variant === "primary") {
    return (
      <Link
        to={to}
        className={`${base} text-black`}
        style={{ background: NEON, boxShadow: `0 10px 40px ${NEON}40` }}
      >
        {children}
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className={`${base} text-white`}
      style={{
        background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DEEP})`,
        boxShadow: `0 10px 40px ${PURPLE}55, inset 0 1px 0 rgba(255,255,255,.15)`,
      }}
    >
      {children}
    </Link>
  );
}

function TrustBullet({ children }) {
  return (
    <li className="flex items-center gap-2 text-sm text-zinc-400">
      <Check className="h-4 w-4 shrink-0" style={{ color: NEON }} />
      <span>{children}</span>
    </li>
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-white/10 p-6 backdrop-blur-xl ${className}`}
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
        boxShadow:
          "0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.03)",
      }}
    >
      {children}
    </div>
  );
}

function ScreenshotSlot() {
  return (
    <div
      className="relative w-full overflow-hidden rounded-xl border"
      style={{
        aspectRatio: "4/3",
        borderColor: HAIRLINE,
        backgroundImage: `url(${ss1})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        boxShadow: `0 30px 80px -40px ${NEON}40`,
      }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="rounded-full border px-3 py-1 text-xs uppercase tracking-wider text-zinc-300"
          style={{ borderColor: HAIRLINE, backgroundColor: "rgba(0,0,0,0.35)" }}
        >
          Founding clipper profile
        </span>
      </div>
    </div>
  );
}

function ValueCard({ icon: Icon, title, children }) {
  return (
    <Card className="h-full transition-colors hover:border-white/15">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ backgroundColor: "rgba(132,255,0,0.1)" }}
      >
        <Icon className="h-5 w-5" style={{ color: NEON }} />
      </div>
      <h3 className="mt-5 text-xl font-semibold text-zinc-100">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">{children}</p>
    </Card>
  );
}

function SubmissionMock() {
  const rows = [
    ["Clip link", "tiktok.com/@clipper/video/..."],
    ["Source timestamp", "01:14:22"],
    ["Platform", "TikTok"],
    ["Caption idea", "He thought the round was over"],
    ["Status", "Pending review"],
  ];

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>
            Clip submission
          </p>
          <h2 className="mt-3 text-2xl font-bold text-white">What you send back</h2>
        </div>
        <Upload className="h-7 w-7" style={{ color: NEON }} />
      </div>
      <div className="mt-5 space-y-3">
        {rows.map(([label, value]) => (
          <div key={label} className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
            <p className="mt-2 text-sm font-medium text-zinc-100">{value}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function StepCard({ n, title, description, icon: Icon, accent = "neon" }) {
  const color = accent === "purple" ? PURPLE : NEON;

  return (
    <div
      className="flex flex-col rounded-xl border p-6 transition-colors hover:border-white/15"
      style={{
        backgroundColor: SURFACE,
        borderColor: `${color}33`,
        boxShadow: `0 20px 60px -40px ${color}50`,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold"
          style={{ backgroundColor: color, color: "#0a0f0d" }}
        >
          {n}
        </div>
        <span className="text-xs font-medium uppercase tracking-wider" style={{ color }}>
          Step {n}
        </span>
      </div>
      <div
        className="mt-5 flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-zinc-100">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">{description}</p>
    </div>
  );
}

export default function ForClippers() {
  return (
    <>
      <SEO
        title="For Clippers | Join Twitch Clipping Campaigns Early"
        description="Join Kre8 early as a founding clipper. Create your profile and get ready for Twitch clipping campaigns built around TikTok, Shorts, and Reels."
        canonicalPath="/for-clippers"
        structuredData={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "For Clippers", path: "/for-clippers" },
        ])}
      />
      <TheNav />
      <main className="min-h-screen antialiased" style={{ backgroundColor: BG, color: "#e5e7eb" }}>
        <Section className="relative overflow-hidden !pt-24 sm:!pt-28">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-80"
            style={{
              background: `radial-gradient(60% 50% at 20% 20%, ${PURPLE}33, transparent 60%), radial-gradient(50% 50% at 85% 30%, ${NEON}1a, transparent 60%), radial-gradient(40% 40% at 50% 90%, ${PURPLE_DEEP}33, transparent 60%)`,
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-[0.06]"
            style={{
              backgroundImage: GRID_BG,
              backgroundSize: "60px 60px",
              maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            }}
          />
          <div className="relative mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="text-center lg:text-left">
              <p
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium"
                style={{
                  borderColor: `${NEON}55`,
                  color: NEON,
                  background: `${NEON}10`,
                  boxShadow: `0 0 24px ${NEON}22`,
                }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Founding clipper access
              </p>
              <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl leading-[1.05]">
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(120deg, ${NEON}, #ffffff 50%, ${PURPLE})`,
                  }}
                >
                  Join early for Twitch clipping campaigns
                </span>
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-zinc-400 sm:text-xl lg:mx-0">
                Create your Kre8 profile now and get ready for streamer campaigns where creators set RPM and clippers submit posted clip links for view tracking.
              </p>
              <ul className="mx-auto mt-8 flex max-w-xl flex-wrap justify-center gap-x-6 gap-y-3 lg:justify-start">
                <TrustBullet>No need to offer long form edits</TrustBullet>
                <TrustBullet>Get paid from approved campaign views</TrustBullet>
                <TrustBullet>Submit links fast after posting</TrustBullet>
              </ul>
              <div className="mt-10 flex flex-wrap justify-center gap-3 lg:justify-start">
                <CTAButton to="/signup?role=clipper&focus=clipping-campaigns">Join as a Founding Clipper</CTAButton>
                <CTAButton to="/clipping-campaigns" variant="secondary">
                  See Campaigns
                </CTAButton>
              </div>
            </div>
            <div className="relative mx-auto w-full max-w-xl lg:max-w-none">
              <ScreenshotSlot />
            </div>
          </div>
        </Section>

        <Section className="!pt-0">
          <div className="grid gap-5 md:grid-cols-3">
            <ValueCard icon={Scissors} title="Clip what you already understand">
              Focus on stream moments, reactions, jokes, plays, and stories that can become TikToks, Shorts, and Reels.
            </ValueCard>
            <ValueCard icon={BarChart3} title="Know the RPM before posting">
              Campaigns show the price per 1,000 views, tracking window, and platform payout rule before you join.
            </ValueCard>
            <ValueCard icon={TrendingUp} title="Post more than one clip">
              One clip can miss. Multiple clips raise your chance of finding the hook that gets picked up.
            </ValueCard>
          </div>
        </Section>

        <Section>
          <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="grid gap-5">
              <ValueCard icon={Clock} title="First 24 to 48 hours">
                Most viral growth happens here if the algorithm picks up your hook. Submit links quickly so tracking starts cleanly.
              </ValueCard>
              <ValueCard icon={Eye} title="7 day check">
                Many campaigns will use the 7 day mark to check initial views and estimate early payouts.
              </ValueCard>
              <ValueCard icon={Calendar} title="30 day cap">
                Standard campaigns usually stop counting views for payment 30 days after the clip is posted.
              </ValueCard>
            </div>
            <SubmissionMock />
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>
              Start simple
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Your reason to sign up today
            </h2>
            <p className="mt-4 text-zinc-400">
              The campaign tools are still rolling out, but your profile can start working now. Show the kind of clips you make, the games you know, and the platforms you edit for.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            <StepCard
              n="1"
              title="Create your profile"
              description="Use the current editor signup flow and position yourself as a clipper in your bio, examples, and category."
              icon={UserPlus}
            />
            <StepCard
              n="2"
              title="Post and submit links"
              description="Publish the clip, then submit the link, timestamp, platform, caption idea, and notes to the campaign."
              icon={Scissors}
              accent="purple"
            />
            <StepCard
              n="3"
              title="Track views and payouts"
              description="Approved campaign views turn into payout estimates based on the creator's RPM and tracking rules."
              icon={Clock}
            />
          </div>
        </Section>

        <Section>
          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <Wallet className="h-7 w-7" style={{ color: NEON }} />
              <h2 className="mt-5 text-2xl font-bold text-white">Honest early access</h2>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                We are not promising instant campaign work today. The goal is to let serious clippers get positioned before view based campaigns open wider.
              </p>
            </Card>
            <Card>
              <MessageSquare className="h-7 w-7" style={{ color: PURPLE }} />
              <h2 className="mt-5 text-2xl font-bold text-white">Direct creator context</h2>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                Kre8 is built around Twitch creators, not random generic gigs. That means your profile can speak to stream pacing, games, humor, and short form growth.
              </p>
            </Card>
          </div>
        </Section>

        <Section>
          <div
            className="relative overflow-hidden rounded-3xl border border-white/10 p-10 text-center sm:p-16"
            style={{
              background: `linear-gradient(135deg, ${PURPLE}55, #0a0a14 50%, ${NEON}33)`,
              boxShadow: `0 30px 100px rgba(0,0,0,0.6), 0 0 100px ${PURPLE}33`,
            }}
          >
            <Scissors className="mx-auto h-10 w-10" style={{ color: NEON }} />
            <h2 className="mx-auto mt-5 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-5xl">
              Join the founding clipper pool
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-zinc-300">
              Create your profile and give streamers a real reason to remember you when clipping campaigns start moving.
            </p>
            <div className="mt-9 flex justify-center">
              <CTAButton to="/signup?role=clipper&focus=clipping-campaigns">Create Your Clipper Profile</CTAButton>
            </div>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
